import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { thresholdService } from '../services/thresholdService';
import { userService, UserWithGrower } from '../services/userService';
import Layout from '../components/Layout';
import { User, Threshold } from '../types';
import { getStoredTempUnit } from '../utils/temperatureUtils';
import {
  buildThresholdLabels,
  getThresholdConditionText,
} from '../utils/thresholdUtils';

// Group thresholds by sensor_id
function groupBySensor(
  thresholds: Threshold[]
): { sensorId: number; sensorName: string; thresholds: Threshold[] }[] {
  const map = new Map<number, { sensorId: number; sensorName: string; thresholds: Threshold[] }>();
  for (const t of thresholds) {
    if (!map.has(t.sensor_id)) {
      map.set(t.sensor_id, {
        sensorId: t.sensor_id,
        sensorName: t.sensor?.name ?? `Sensor #${t.sensor_id}`,
        thresholds: [],
      });
    }
    map.get(t.sensor_id)!.thresholds.push(t);
  }
  return Array.from(map.values());
}

// ---------- component ----------
const ThresholdsOverview: React.FC = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  const [allUsers, setAllUsers] = useState<UserWithGrower[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [searchSensor, setSearchSensor] = useState('');
  const [filterCreatedBy, setFilterCreatedBy] = useState<string>('');   // user id as string, '' = all
  const [filterType, setFilterType] = useState<string>('');             // ThresholdTypeValue | ''
  const [filterStatus, setFilterStatus] = useState<string>('');         // 'active' | 'inactive' | ''
  const [filterAlerts, setFilterAlerts] = useState<string>('');         // 'has_alerts' | 'no_alerts' | ''

  // Collapsed sensor groups (by sensor_id)
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const tempUnit = getStoredTempUnit();
  const THRESHOLD_LABELS = useMemo(() => buildThresholdLabels(tempUnit), [tempUnit]);

  useEffect(() => {
    const init = async () => {
      try {
        const [currentUser, allThresholds] = await Promise.all([
          authService.getCurrentUser(),
          thresholdService.getThresholds(),
        ]);
        setUser(currentUser);
        setThresholds(allThresholds);
        // Collapse all sensor groups by default
        setCollapsed(new Set(allThresholds.map((thr) => thr.sensor_id)));

        // Admins can filter by user
        if (currentUser.is_admin) {
          const users = await userService.getUsers();
          setAllUsers(users);
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || t('thresholdsOverview.loadError'));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [t]);

  // Derive unique threshold types present
  const availableTypes = useMemo(() => {
    const types = new Set<string>(thresholds.map((thr) => thr.threshold_type));
    return Array.from(types).sort();
  }, [thresholds]);

  // Apply all filters
  const filtered = useMemo(() => {
    return thresholds.filter((thr) => {
      // Search on sensor name (case-insensitive)
      if (searchSensor) {
        const sName = thr.sensor?.name ?? '';
        if (!sName.toLowerCase().includes(searchSensor.toLowerCase())) return false;
      }
      // Created-by filter
      if (filterCreatedBy && String(thr.created_by_id) !== filterCreatedBy) return false;
      // Type filter
      if (filterType && thr.threshold_type !== filterType) return false;
      // Status filter
      if (filterStatus === 'active' && !thr.is_active) return false;
      if (filterStatus === 'inactive' && thr.is_active) return false;
      // Alert filter
      if (filterAlerts === 'has_alerts' && thr.active_alert_count === 0) return false;
      if (filterAlerts === 'no_alerts' && thr.active_alert_count > 0) return false;
      return true;
    });
  }, [thresholds, searchSensor, filterCreatedBy, filterType, filterStatus, filterAlerts]);

  const grouped = useMemo(() => groupBySensor(filtered), [filtered]);

  // Summary counts
  const totalAlerts = useMemo(
    () => thresholds.reduce((sum, thr) => sum + thr.active_alert_count, 0),
    [thresholds]
  );

  const toggleCollapse = (sensorId: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(sensorId)) next.delete(sensorId);
      else next.add(sensorId);
      return next;
    });
  };

  const clearFilters = () => {
    setSearchSensor('');
    setFilterCreatedBy('');
    setFilterType('');
    setFilterStatus('');
    setFilterAlerts('');
  };

  const hasActiveFilters =
    searchSensor || filterCreatedBy || filterType || filterStatus || filterAlerts;

  if (loading) {
    return (
      <Layout user={user || undefined}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user || undefined}>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('thresholdsOverview.title')}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('thresholdsOverview.summary', {
                sensors: grouped.length,
                thresholds: thresholds.length,
                alerts: totalAlerts,
              })}
            </p>
          </div>
          {totalAlerts > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-red-100 border border-red-300 rounded-lg">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold">
                {totalAlerts}
              </span>
              <span className="text-sm font-medium text-red-800">
                {t('thresholdsOverview.activeAlertsTotal', { count: totalAlerts })}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Filters bar */}
        <div className="bg-white shadow rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Search sensor name */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('thresholdsOverview.filterSearchSensor')}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchSensor}
                  onChange={(e) => setSearchSensor(e.target.value)}
                  placeholder={t('thresholdsOverview.filterSearchPlaceholder')}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <svg
                  className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Filter by type */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('thresholdsOverview.filterType')}
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">{t('thresholdsOverview.allTypes')}</option>
                {availableTypes.map((type) => (
                  <option key={type} value={type}>
                    {THRESHOLD_LABELS[type] || type}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter by status */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('thresholdsOverview.filterStatus')}
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">{t('thresholdsOverview.allStatuses')}</option>
                <option value="active">{t('common.active')}</option>
                <option value="inactive">{t('common.inactive')}</option>
              </select>
            </div>

            {/* Filter by alerts */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('thresholdsOverview.filterAlerts')}
              </label>
              <select
                value={filterAlerts}
                onChange={(e) => setFilterAlerts(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">{t('thresholdsOverview.allAlertStates')}</option>
                <option value="has_alerts">{t('thresholdsOverview.withAlerts')}</option>
                <option value="no_alerts">{t('thresholdsOverview.withoutAlerts')}</option>
              </select>
            </div>
          </div>

          {/* Second row: user filter (admin only) + clear */}
          {user?.is_admin && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('thresholdsOverview.filterCreatedBy')}
                </label>
                <select
                  value={filterCreatedBy}
                  onChange={(e) => setFilterCreatedBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">{t('thresholdsOverview.allUsers')}</option>
                  {allUsers.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.full_name || u.email}
                    </option>
                  ))}
                </select>
              </div>
              {hasActiveFilters && (
                <div className="flex justify-start sm:justify-end">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {t('thresholdsOverview.clearFilters')}
                  </button>
                </div>
              )}
            </div>
          )}

          {!user?.is_admin && hasActiveFilters && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('thresholdsOverview.clearFilters')}
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        {grouped.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-10 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="mt-4 text-gray-500">
              {hasActiveFilters
                ? t('thresholdsOverview.noResults')
                : t('thresholdsOverview.noThresholds')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map((group) => {
              const groupAlerts = group.thresholds.reduce(
                (sum, thr) => sum + thr.active_alert_count,
                0
              );
              const isCollapsed = collapsed.has(group.sensorId);

              return (
                <div
                  key={group.sensorId}
                  className="bg-white shadow rounded-lg overflow-hidden"
                >
                  {/* Sensor group header */}
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                    onClick={() => toggleCollapse(group.sensorId)}
                    aria-expanded={!isCollapsed}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <svg
                        className={`h-4 w-4 text-gray-400 shrink-0 transition-transform ${
                          isCollapsed ? '-rotate-90' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      <span className="font-semibold text-gray-900 truncate">
                        {group.sensorName}
                      </span>
                      <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {t('thresholdsOverview.thresholdCount', {
                          count: group.thresholds.length,
                        })}
                      </span>
                      {groupAlerts > 0 && (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-600 text-white text-xs font-bold">
                            {groupAlerts}
                          </span>
                          {t('thresholdsOverview.activeAlerts', { count: groupAlerts })}
                        </span>
                      )}
                    </div>
                    <Link
                      to={`/sensors/${group.sensorId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0 ml-4 text-sm text-primary-600 hover:text-primary-800 hover:underline"
                    >
                      {t('thresholdsOverview.viewSensor')}
                    </Link>
                  </button>

                  {/* Threshold rows */}
                  {!isCollapsed && (
                    <div className="border-t border-gray-100 divide-y divide-gray-50">
                      {group.thresholds.map((thr) => (
                        <ThresholdRow
                          key={thr.id}
                          threshold={thr}
                          labels={THRESHOLD_LABELS}
                          tempUnit={tempUnit}
                          t={t}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

// ---------- ThresholdRow sub-component ----------
interface ThresholdRowProps {
  threshold: Threshold;
  labels: Record<string, string>;
  tempUnit: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const ThresholdRow: React.FC<ThresholdRowProps> = ({ threshold, labels, tempUnit, t }) => {
  const conditionText = getThresholdConditionText(threshold, tempUnit);
  const typeLabel = labels[threshold.threshold_type] || threshold.threshold_type;
  const hasAlert = threshold.active_alert_count > 0;

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 ${
        hasAlert ? 'bg-red-50' : 'hover:bg-gray-50'
      } transition-colors`}
    >
      {/* Alert indicator stripe */}
      {hasAlert && (
        <div className="hidden sm:block w-1 h-10 rounded-full bg-red-500 shrink-0" />
      )}

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`font-medium text-sm ${hasAlert ? 'text-red-900' : 'text-gray-900'}`}>
            {threshold.name}
          </span>
          {hasAlert && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {t('thresholdsOverview.alertBadge', { count: threshold.active_alert_count })}
            </span>
          )}
          <span
            className={`inline-block px-2 py-0.5 text-xs rounded-full ${
              threshold.is_active
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            {threshold.is_active ? t('common.active') : t('common.inactive')}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          {typeLabel} · {conditionText}
        </p>
        {threshold.created_by && (
          <p className="text-xs text-gray-400 mt-0.5">
            {t('sensorDetail.createdBy')}:{' '}
            {threshold.created_by.full_name || threshold.created_by.email}
          </p>
        )}
      </div>

      {/* Dates */}
      <div className="shrink-0 text-right hidden lg:block">
        <p className="text-xs text-gray-400">
          {t('sensorDetail.createdAt')}:{' '}
          {new Date(threshold.created_at).toLocaleDateString()}
        </p>
        {threshold.updated_at && (
          <p className="text-xs text-gray-400 mt-0.5">
            {t('sensorDetail.lastEdited')}:{' '}
            {new Date(threshold.updated_at).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default ThresholdsOverview;
